import { FileSpreadsheet, FileUp, ListChecks } from 'lucide-react'
import { useId, useRef, useState } from 'react'

import { Button, CheckboxField, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import type { MappingEditorVm, WizardActions, WizardVm } from '../model/types'
import '../../pricing/ui/onec-ui.css'
import { ReportView } from './ReportView'

function ErrorBox({ title, error }: { title: string; error: { message: string; code: string; correlationId: string | null } }) {
  return (
    <StatusMessage
      kind="error"
      title={title}
      text={error.message}
      meta={`Код: ${error.code}${error.correlationId === null ? '' : ' · запрос ' + error.correlationId}`}
    />
  )
}

function UploadForm({ vm, actions }: { vm: WizardVm; actions: WizardActions }) {
  const fileId = useId()
  // Выбранный файл — не данные сервера и не намерение для хранения: живёт в поле ввода.
  const [file, setFile] = useState<File | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const { upload } = vm
  return (
    <section className="onec-section" aria-labelledby="onec-import-upload">
      <h3 id="onec-import-upload">Загрузка накладной</h3>
      {/* Системное поле выбора файла говорит языком браузера («Choose File / No file chosen»): оно скрыто, выбор —
          своей кнопкой, имя файла — словами экрана (аудит 15.10, п. 14). Подпись остаётся у поля для диктора. */}
      <div className="krp-field onec-file">
        <label className="krp-field__label" htmlFor={fileId}>
          Файл Excel (.xlsx)
        </label>
        <input
          ref={input}
          id={fileId}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        <div className="onec-file__row">
          <Button icon={<FileSpreadsheet size={16} aria-hidden="true" />} onClick={() => input.current?.click()}>
            {file === null ? 'Выбрать файл' : 'Другой файл'}
          </Button>
          <span className="onec-file__name" aria-live="polite">
            {file === null ? 'Файл не выбран' : file.name}
          </span>
        </div>
      </div>
      <div className="onec-row">
        <TextField label="Лист (по умолчанию — первый непустой)" value={upload.params.sheet} onChange={(sheet) => actions.paramsChanged({ sheet })} />
        <TextField label="Строка шапки (0 — шапки нет)" value={upload.params.headerRow} onChange={(headerRow) => actions.paramsChanged({ headerRow })} />
        <SelectField label="Профиль колонок" value={upload.params.profileId} options={upload.profileOptions} onChange={(profileId) => actions.paramsChanged({ profileId })} />
        <TextField label="Поставщик" value={upload.params.supplier} onChange={(supplier) => actions.paramsChanged({ supplier })} />
      </div>
      {upload.unknownOutcome && (
        <StatusMessage
          kind="unavailable"
          title="Неизвестно, загружен ли файл"
          text="Ответ не получен. Повторная загрузка того же файла безопасна: она уйдёт с теми же ключами, и второй файл не создастся."
        />
      )}
      {upload.error !== null && !upload.unknownOutcome && <ErrorBox title="Файл не загружен" error={upload.error} />}
      <div className="onec-row">
        <Button
          variant="primary"
          icon={<FileUp size={16} aria-hidden="true" />}
          busy={upload.busy}
          disabled={file === null || vm.configurationVersion === null}
          onClick={() => {
            if (file !== null) actions.upload(file)
          }}
        >
          {upload.unknownOutcome ? 'Повторить загрузку' : 'Загрузить'}
        </Button>
        <Button variant="ghost" onClick={actions.openProfiles}>
          Профили колонок
        </Button>
      </div>
    </section>
  )
}

function MappingEditor({ editor, actions }: { editor: MappingEditorVm; actions: WizardActions }) {
  const { draft } = editor
  return (
    <section className="onec-section" aria-labelledby="onec-import-mapping">
      <h3 id="onec-import-mapping">Разметка колонок</h3>
      <div className="onec-row">
        <SelectField
          label="Лист"
          value={editor.sheetName}
          options={editor.worksheets.map((name) => ({ value: name, label: name }))}
          onChange={(sheetName) => actions.mappingChanged({ sheetName })}
        />
        <TextField label="Строка шапки" value={editor.headerRow} onChange={(headerRow) => actions.mappingChanged({ headerRow })} />
      </div>
      <div className="onec-scroll">
        <table className="onec-table">
          <thead>
            <tr>
              <th>Колонка</th>
              <th>Заголовок в файле</th>
              <th>Роль</th>
            </tr>
          </thead>
          <tbody>
            {editor.columns.map((column) => (
              <tr key={column.columnIndex}>
                <td>{column.columnText}</td>
                <td>
                  {column.headerText || '—'}
                  {column.suggestionText !== null && <span className="onec-muted"> ({column.suggestionText})</span>}
                </td>
                <td>
                  <SelectField
                    label={`Роль: ${column.columnText}`}
                    value={column.role}
                    options={editor.roleOptions}
                    onChange={(value) => {
                      const option = editor.roleOptions.find((candidate) => candidate.value === value)
                      if (option !== undefined) actions.roleChanged(column.columnIndex, option.value)
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editor.missingRequired.length > 0 && (
        <p className="onec-problem">Не размечено обязательное: {editor.missingRequired.join(', ')}</p>
      )}
      <div className="onec-row">
        <CheckboxField label="Цена с НДС" checked={draft.priceIncludesVat} onChange={(priceIncludesVat) => actions.mappingChanged({ priceIncludesVat })} />
        <CheckboxField label="Количество в упаковках" checked={draft.quantityInPackages} onChange={(quantityInPackages) => actions.mappingChanged({ quantityInPackages })} />
        <CheckboxField label="Сохранить как профиль" checked={draft.saveAsProfile} onChange={(saveAsProfile) => actions.mappingChanged({ saveAsProfile })} />
      </div>
      {draft.saveAsProfile && (
        <div className="onec-row">
          <TextField label="Имя профиля" value={draft.profileName} onChange={(profileName) => actions.mappingChanged({ profileName })} />
          <TextField label="Поставщик профиля" value={draft.supplierName} onChange={(supplierName) => actions.mappingChanged({ supplierName })} />
        </div>
      )}
      {editor.problem !== null && <p className="onec-problem">{editor.problem}</p>}
      {editor.unknownOutcome && (
        <StatusMessage kind="unavailable" title="Неизвестно, применена ли разметка" text="Ответ не получен. Повтор безопасен: он уйдёт с теми же ключами." />
      )}
      {editor.error !== null && !editor.unknownOutcome && <ErrorBox title="Разметка не применена" error={editor.error} />}
      <div className="onec-row">
        <Button variant="primary" icon={<ListChecks size={16} aria-hidden="true" />} busy={editor.busy} disabled={!editor.canApply} onClick={actions.applyMapping}>
          {editor.unknownOutcome ? 'Повторить' : 'Применить разметку'}
        </Button>
      </div>
    </section>
  )
}

/** Окно «Импорт накладной»: загрузка → разметка колонок → отчёт проверки. */
export function ImportWizardScreen({ vm, actions }: { vm: WizardVm; actions: WizardActions }) {
  return (
    <section className="onec-screen" aria-labelledby="onec-import-title">
      <header className="onec-section__head">
        <h2 id="onec-import-title">Импорт накладной</h2>
      </header>
      <UploadForm vm={vm} actions={actions} />
      {vm.parsed !== null && (
        <>
          <section className="onec-section" aria-labelledby="onec-import-file">
            <div className="onec-section__head">
              <h3 id="onec-import-file">{vm.parsed.fileName}</h3>
              <Button onClick={actions.openFile}>Открыть файл</Button>
            </div>
            <p>
              {vm.parsed.matchedByText}
              {vm.parsed.profileText !== null && `: ${vm.parsed.profileText}`}
            </p>
            {vm.parsed.notes.length > 0 && (
              <ul className="onec-muted">
                {vm.parsed.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}
          </section>
          <MappingEditor editor={vm.parsed.editor} actions={actions} />
          <section className="onec-section" aria-labelledby="onec-import-preview">
            <h3 id="onec-import-preview">Строки накладной</h3>
            <div className="onec-scroll">
              <table className="onec-table">
                <thead>
                  <tr>
                    <th data-numeric="true">№</th>
                    <th>Наименование</th>
                    <th>Артикул</th>
                    <th>Штрихкод</th>
                    <th data-numeric="true">Кол-во</th>
                    <th data-numeric="true">Цена</th>
                    <th data-numeric="true">Сумма</th>
                    <th>Примечания</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.parsed.preview.map((row) => (
                    <tr key={row.rowNumber}>
                      <td data-numeric="true">{row.rowNumber}</td>
                      <td>{row.name}</td>
                      <td>{row.sku}</td>
                      <td>{row.barcode}</td>
                      <td data-numeric="true">{row.quantityText}</td>
                      <td data-numeric="true">{row.priceText}</td>
                      <td data-numeric="true">{row.sumText}</td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {vm.parsed.previewTruncated && <p className="onec-muted">Показаны первые строки накладной</p>}
          </section>
          {vm.parsed.report !== null && (
            <section className="onec-section" aria-labelledby="onec-import-report">
              <h3 id="onec-import-report">Отчёт проверки</h3>
              <ReportView report={vm.parsed.report} emptyText="Отчёта нет" />
            </section>
          )}
        </>
      )}
    </section>
  )
}
